import { zodResolver } from '@hookform/resolvers/zod';
import {
  IQuoteSettingContractSchema,
  QuoteSettingContractSchema,
} from '@monetize/types/app';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  MAccordion,
  MAccordionCustomButton,
  MBox,
  MButton,
  MFlex,
  MStack,
} from '../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl } from '../../../../../hooks';
import { useScrollToHighlightedSection } from '../../../../../hooks/useScrollToHighlightedSection';
import { getHighlightStyles } from '../../../../../hooks/useSettingsSectionHighlight';
import { useSettingsSectionHighlights } from '../../../../../hooks/useSettingsSectionHighlights';
import {
  QUOTE_SETTING_KEYS,
  getQuoteSettingsSectionKeys,
} from '../quoteSettingsConfig';
import { ContractRenewalTermsForm } from './ContractRenewalTermsForm';

interface QuoteSettingEndActionProps {
  value: IQuoteSettingContractSchema;
  onSubmit: (data: IQuoteSettingContractSchema) => Promise<void>;
  isLoading?: boolean;
}

export const QuoteSettingEndAction = ({
  value,
  onSubmit,
  isLoading,
}: QuoteSettingEndActionProps) => {
  const highlights = useSettingsSectionHighlights(
    getQuoteSettingsSectionKeys(QUOTE_SETTING_KEYS.endAction),
  );
  const contractSettingForm = useForm<IQuoteSettingContractSchema>({
    resolver: zodResolver(QuoteSettingContractSchema),
    mode: 'onChange',
    defaultValues: value,
    values: value,
  });
  const {
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
  } = contractSettingForm;

  useEffect(() => {
    if (value) {
      reset({
        ...value,
      });
    }
  }, [reset, value]);

  const onSave = async (data: IQuoteSettingContractSchema) => {
    try {
      await onSubmit(data);
    } catch (err: any) {
      handleApiErrorToast(err);
    }
  };

  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useScrollToHighlightedSection(highlights, !isLoading);

  const shouldOpenAccordion = Object.values(highlights).some(Boolean);
  useEffect(() => {
    if (shouldOpenAccordion) {
      setAccordionValue(['0']);
    }
  }, [shouldOpenAccordion, setAccordionValue]);

  return (
    <MStack w="100%" data-testid="contract-setting-config">
      <form onSubmit={handleSubmit(onSave)} data-testid="contract-setting-form">
        <MAccordion
          multiple
          value={accordionValue}
          onValueChange={({ value }) => handleAccordionChange(value)}
        >
          <MSettingAccordionItem
            value="0"
            id={QUOTE_SETTING_KEYS.endAction}
            {...getHighlightStyles(highlights[QUOTE_SETTING_KEYS.endAction])}
            renderAccordionButton={({
              isExpanded,
            }: {
              isExpanded: boolean;
            }) => (
              <MAccordionCustomButton
                isExpanded={isExpanded}
                label="Renewal Action"
              />
            )}
            borderWidth="1px"
          >
            <MBox mt="4">
              <ContractRenewalTermsForm
                contractSettingForm={contractSettingForm}
                highlights={highlights}
              />

              <MFlex justify="end">
                <MButton
                  variant="primary"
                  onClick={handleSubmit(onSave)}
                  isDisabled={!isDirty || !isValid}
                  isLoading={isLoading}
                >
                  Save
                </MButton>
              </MFlex>
            </MBox>
          </MSettingAccordionItem>
        </MAccordion>
      </form>
    </MStack>
  );
};
